import React from 'react';
import { useIntl } from 'react-intl';
import { getCompanyLogoSrcset, getCompanyLogoDefaultSrc, getCompanyLogoSources } from '../utils/company/get-company-logo-url';
import { formatCompanyLogoAltLabelFromProps } from '../utils/company/format-company-logo-alt-label-from-props';
import {
  getCompanyLogoFormShortLabel,
  getCompanyLogoScriptDisplayName,
} from '../utils/company/company-logo-fallback-content';
import { CompanyLogoScriptFallback } from './CompanyLogoScriptFallback';
import { classifyLogoTileBackdropFromHtmlImage, type LogoTileBackdrop } from '../utils/company/logo-tile-backdrop/classify-logo-tile-backdrop';
import { optimizeHostedLogoUrl } from '../utils/company/optimize-hosted-logo-url';

/** Pixel size or preset matching job-detail sizes (small 40 … xlarge 128). */
export type CompanyLogoSize = number | 'small' | 'medium' | 'large' | 'xlarge';

const LOGO_SIZE_PRESETS: Record<Exclude<CompanyLogoSize, number>, number> = {
  small: 40,
  medium: 56,
  large: 96,
  xlarge: 128,
};

function resolveLogoSize(size: CompanyLogoSize | undefined, fallback: number): number {
  if (size === undefined) return fallback;
  if (typeof size === 'number') return size;
  return LOGO_SIZE_PRESETS[size];
}

export interface CompanyLogoProps {
  /** Company display id / name used for logo URL (preferred). */
  companyName?: string;
  /** Alias for `companyName` (e.g. job detail payloads). */
  companyId?: string;
  /** Renders nothing when both identifiers are missing. */
  size?: CompanyLogoSize;
  alt?: string;
  className?: string;
  /** `bare` omits the border/shadow and inherits its radius from the parent tile — for callers
   * that already wrap the logo in their own bordered/backgrounded tile (a brand-color rail, a
   * podium card, etc.), where CompanyLogo's own border read as a redundant second ring. */
  variant?: 'default' | 'rounded' | 'square' | 'bare';
  isTyomarkkinatori?: boolean | 'true' | 'false';
  loading?: 'eager' | 'lazy';
  /** When set with `disableLink` false, wraps the logo in an external link. */
  companyURL?: string;
  disableLink?: boolean;
  /** `title` on the external link (defaults to company key). */
  linkTitle?: string;
  /** Name used for script fallback + legal-form parsing when it differs from the logo URL key (e.g. marketing `company` vs facet id). */
  initialsSourceName?: string;
  /** Trade register legal form line — shown as a short ribbon on generated avatars. */
  registryCompanyFormDescription?: string | null;
  /** Overrides parsed legal form (e.g. "Oy"). */
  companyFormShortLabel?: string | null;
  /** Fires once when the hosted WebP/SVG asset fails (including decode / zero-size after load). */
  onHostedLogoFailure?: () => void;
  /**
   * When set, loads this URL directly instead of deriving `/logos/{kebab-case}-*.webp` from the company key.
   * Used for search hits that carry an external or pre-resolved logo URL from the index.
   */
  hostedLogoUrl?: string;
}

/**
 * Company logo: WebP srcset, then script-style name + legal-form tile if the image fails.
 */
export const CompanyLogo: React.FC<CompanyLogoProps> = ({
  companyName,
  companyId,
  size = 40,
  alt,
  variant = 'default',
  className = '',
  isTyomarkkinatori: isTyomarkkinatoriProp,
  loading = 'lazy',
  companyURL,
  disableLink = false,
  linkTitle,
  initialsSourceName,
  registryCompanyFormDescription,
  companyFormShortLabel,
  onHostedLogoFailure,
  hostedLogoUrl,
}) => {
  const intl = useIntl();
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const companyKey = (companyName ?? companyId ?? '').trim();
  const pxSize = resolveLogoSize(size, 40);

  const nameForInitials = (initialsSourceName ?? '').trim() || companyKey;

  const isTyomarkkinatori = React.useMemo(() => {
    if (isTyomarkkinatoriProp === true || isTyomarkkinatoriProp === 'true') {
      return true;
    }
    if (isTyomarkkinatoriProp === false || isTyomarkkinatoriProp === 'false') {
      return false;
    }
    const normalized = companyKey.toLowerCase();
    return normalized === 'työmarkkinatori' || normalized === 'tyomarkkinatori';
  }, [companyKey, isTyomarkkinatoriProp]);

  const hostedFallbackUrl = optimizeHostedLogoUrl((hostedLogoUrl ?? '').trim(), pxSize);

  // Our own processed/hosted logo (validated + resized when the company's logo was processed)
  // is always preferred over the raw URL a search hit may carry — that raw URL is an
  // unvalidated, potentially stale per-job snapshot. It's only used as a fallback when our
  // hosted asset fails to load, or when there's no company key to derive one from at all
  // (e.g. some external aggregator hits with no matching company record).
  const sources = React.useMemo(() => {
    const list: Array<{ src: string; srcSet: string }> = [];
    if (companyKey) {
      list.push({
        src: getCompanyLogoDefaultSrc(companyKey, isTyomarkkinatori),
        srcSet: getCompanyLogoSrcset(companyKey, isTyomarkkinatori),
      });
    }
    if (hostedFallbackUrl) {
      list.push({ src: hostedFallbackUrl, srcSet: '' });
    }
    return list;
  }, [companyKey, isTyomarkkinatori, hostedFallbackUrl]);

  const sourceIndexRef = React.useRef(0);
  const [sourceIndex, setSourceIndex] = React.useState(0);
  const [logoFailed, setLogoFailed] = React.useState(false);
  const [logoTileBackdrop, setLogoTileBackdrop] = React.useState<LogoTileBackdrop>('green');
  const attemptHandledRef = React.useRef(false);

  const activeSource = sources[sourceIndex];
  const currentSrc = activeSource?.src ?? '';
  const currentSrcSet = activeSource?.srcSet ?? '';

  const scriptLine = React.useMemo(() => {
    const s = getCompanyLogoScriptDisplayName(nameForInitials);
    return (s || companyKey || '?').trim();
  }, [nameForInitials, companyKey]);
  const formLabel = React.useMemo(
    () => getCompanyLogoFormShortLabel(nameForInitials, registryCompanyFormDescription, companyFormShortLabel),
    [nameForInitials, registryCompanyFormDescription, companyFormShortLabel]
  );

  const label = formatCompanyLogoAltLabelFromProps(intl, {
    alt,
    initialsSourceName,
    companyName,
    companyId,
  });

  // Moves to the next candidate source when the current one fails; only shows the initials
  // fallback once every source (hosted, then raw) has been exhausted.
  const advanceSource = React.useCallback(() => {
    if (attemptHandledRef.current) return;
    attemptHandledRef.current = true;
    const nextIndex = sourceIndexRef.current + 1;
    if (nextIndex < sources.length) {
      sourceIndexRef.current = nextIndex;
      setSourceIndex(nextIndex);
    } else {
      setLogoFailed(true);
      onHostedLogoFailure?.();
    }
  }, [sources.length, onHostedLogoFailure]);

  React.useEffect(() => {
    sourceIndexRef.current = 0;
    attemptHandledRef.current = false;
    setSourceIndex(0);
    setLogoFailed(false);
    setLogoTileBackdrop('green');
  }, [companyKey, hostedFallbackUrl, isTyomarkkinatori]);

  React.useEffect(() => {
    attemptHandledRef.current = false;
  }, [sourceIndex]);

  React.useEffect(() => {
    if (!currentSrcSet || logoFailed) return;

    const timeout = setTimeout(() => {
      if (logoFailed) return;
      if (imgRef.current) {
        const img = imgRef.current;
        if (img.complete && img.naturalWidth === 0 && img.naturalHeight === 0) {
          advanceSource();
        }
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [currentSrcSet, sourceIndex, logoFailed, advanceSource]);

  const handleError = React.useCallback(() => {
    advanceSource();
  }, [advanceSource]);

  const applyBackdropFromImage = React.useCallback((img: HTMLImageElement) => {
    queueMicrotask(() => {
      setLogoTileBackdrop(classifyLogoTileBackdropFromHtmlImage(img));
    });
  }, []);

  const handleImgLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      applyBackdropFromImage(e.currentTarget);
    },
    [applyBackdropFromImage]
  );

  React.useLayoutEffect(() => {
    const img = imgRef.current;
    if (logoFailed || !img?.complete || img.naturalWidth === 0) return;
    applyBackdropFromImage(img);
  }, [companyKey, currentSrc, logoFailed, applyBackdropFromImage]);

  const containerShell = {
    default: 'border border-jc-border rounded-2xl overflow-hidden shadow-sm dark:shadow-none',
    rounded: 'border border-jc-border rounded-xl overflow-hidden shadow-sm dark:shadow-none',
    square: 'border border-jc-border overflow-hidden',
    bare: 'overflow-hidden',
  } as const;

  const borderRadius =
    variant === 'bare' ? 'inherit' : variant === 'square' ? '0' : variant === 'rounded' ? '0.75rem' : '1rem';

  const containerStyle: React.CSSProperties = isTyomarkkinatori
    ? {
        width: pxSize,
        height: pxSize,
        padding: '0',
        borderRadius,
        boxSizing: 'border-box',
        ...(logoFailed
          ? {}
          : {
              backgroundColor: '#6B46C1',
              boxShadow: '0 2px 8px 0 rgba(107, 70, 193, 0.2)',
            }),
      }
    : {
        width: pxSize,
        height: pxSize,
        padding: '0',
        borderRadius,
        boxSizing: 'border-box',
      };

  const containerClassName = isTyomarkkinatori
    ? logoFailed
      ? [containerShell[variant]].join(' ')
      : ''
    : [
        !logoFailed
          ? logoTileBackdrop === 'monoLight'
            ? 'bg-jc-logo-mono-light'
            : logoTileBackdrop === 'monoDark'
              ? 'bg-jc-logo-mono-dark'
              : logoTileBackdrop === 'surface'
                ? 'bg-jc-logo-surface'
              : ''
          : '',
        containerShell[variant],
      ]
        .filter(Boolean)
        .join(' ');

  if (sources.length === 0) {
    const borderRadius =
      variant === 'bare' ? 'inherit' : variant === 'square' ? '0' : variant === 'rounded' ? '0.75rem' : '1rem';
    const shell = containerShellForVariant(variant);
    return (
      <div
        className={`flex items-center justify-center ${shell} ${className} relative`}
        style={{
          width: pxSize,
          height: pxSize,
          padding: 0,
          borderRadius,
          boxSizing: 'border-box',
        }}
      >
        <img
          src="/company-logo-default.svg"
          alt={label}
          width={pxSize}
          height={pxSize}
          className="w-full h-full object-contain"
          loading={loading}
          decoding="async"
          style={{
            background: 'transparent',
            borderRadius,
            aspectRatio: '1 / 1',
          }}
        />
      </div>
    );
  }

  /** Bordered wrapper uses 1px rule; keep avatar slightly inset so radius + border don’t clip. */
  const fallbackAvatarPx = Math.max(8, pxSize - 2);

  const logoInner = (
    <div
      className={`flex items-center justify-center ${containerClassName} ${className} relative`}
      style={containerStyle}
    >
      {!logoFailed ? (
        <img
          ref={imgRef}
          src={currentSrc}
          srcSet={currentSrcSet || undefined}
          sizes={currentSrcSet ? `${pxSize}px` : undefined}
          alt={label}
          width={pxSize}
          height={pxSize}
          className="w-full h-full object-contain"
          onError={handleError}
          onLoad={handleImgLoad}
          onAbort={handleError}
          loading={loading}
          decoding="async"
          style={{
            background: 'transparent',
            borderRadius,
            aspectRatio: '1 / 1',
          }}
        />
      ) : (
        <CompanyLogoScriptFallback
          key={`${scriptLine}\0${fallbackAvatarPx}\0${formLabel}`}
          sizePx={fallbackAvatarPx}
          variant={variant}
          scriptLine={scriptLine}
          formLabel={formLabel}
          theme={isTyomarkkinatori ? 'purple' : 'green'}
          aria-label={label}
        />
      )}
    </div>
  );

  if (companyURL && !disableLink) {
    return (
      <a
        href={companyURL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block"
        title={linkTitle ?? companyKey}
        onClick={(e) => e.stopPropagation()}
      >
        {logoInner}
      </a>
    );
  }

  return logoInner;
};

function containerShellForVariant(variant: CompanyLogoProps['variant']): string {
  const map = {
    default: 'border border-jc-border rounded-2xl overflow-hidden shadow-sm dark:shadow-none',
    rounded: 'border border-jc-border rounded-xl overflow-hidden shadow-sm dark:shadow-none',
    square: 'border border-jc-border overflow-hidden',
    bare: 'overflow-hidden',
  } as const;
  return map[variant ?? 'default'];
}

type IconFallbackProps = Omit<CompanyLogoProps, 'companyURL' | 'disableLink' | 'linkTitle'> & {
  companyName?: string;
  companyId?: string;
};

/**
 * Tries legacy PNG URL chain; ends on the same script avatar as `CompanyLogo`.
 */
export const CompanyLogoWithIconFallback: React.FC<IconFallbackProps> = ({
  companyName,
  companyId,
  size = 40,
  alt,
  className = '',
  variant = 'rounded',
  isTyomarkkinatori: isTyomarkkinatoriProp,
  initialsSourceName,
  registryCompanyFormDescription,
  companyFormShortLabel,
}) => {
  const intl = useIntl();
  const companyKey = (companyName ?? companyId ?? '').trim();
  const pxSize = resolveLogoSize(size, 40);
  const nameForInitials = (initialsSourceName ?? '').trim() || companyKey;

  const isTyomarkkinatori = React.useMemo(() => {
    if (isTyomarkkinatoriProp !== undefined) return isTyomarkkinatoriProp;
    const normalized = companyKey.toLowerCase();
    return normalized === 'työmarkkinatori' || normalized === 'tyomarkkinatori';
  }, [companyKey, isTyomarkkinatoriProp]);

  const scriptLine = React.useMemo(() => {
    const s = getCompanyLogoScriptDisplayName(nameForInitials);
    return (s || companyKey || '?').trim();
  }, [nameForInitials, companyKey]);
  const formLabel = React.useMemo(
    () => getCompanyLogoFormShortLabel(nameForInitials, registryCompanyFormDescription, companyFormShortLabel),
    [nameForInitials, registryCompanyFormDescription, companyFormShortLabel]
  );

  const label = formatCompanyLogoAltLabelFromProps(intl, {
    alt,
    initialsSourceName,
    companyName,
    companyId,
  });

  const [imgError, setImgError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [logoTileBackdrop, setLogoTileBackdrop] = React.useState<LogoTileBackdrop>('green');
  const iconImgRef = React.useRef<HTMLImageElement | null>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const srcIndexRef = React.useRef(0);
  const sources = React.useMemo(
    () => (companyKey ? getCompanyLogoSources(companyKey).slice(0, -1) : []),
    [companyKey]
  );
  const [srcIndex, setSrcIndex] = React.useState(0);

  const clearLoadingTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const setLoadingTimeout = React.useCallback(() => {
    clearLoadingTimeout();
    timeoutRef.current = setTimeout(() => {
      const currentIndex = srcIndexRef.current;
      if (currentIndex < sources.length - 1) {
        const nextIndex = currentIndex + 1;
        srcIndexRef.current = nextIndex;
        setSrcIndex(nextIndex);
        setIsLoading(true);
        setLoadingTimeout();
      } else {
        setImgError(true);
        setIsLoading(false);
      }
    }, 3000);
  }, [sources.length]);

  const handleError = React.useCallback(() => {
    clearLoadingTimeout();
    const currentIndex = srcIndexRef.current;
    if (currentIndex < sources.length - 1) {
      const nextIndex = currentIndex + 1;
      srcIndexRef.current = nextIndex;
      setSrcIndex(nextIndex);
      setIsLoading(true);
      setLoadingTimeout();
    } else {
      setImgError(true);
      setIsLoading(false);
    }
  }, [sources.length, setLoadingTimeout]);

  const applyIconBackdrop = React.useCallback((img: HTMLImageElement) => {
    queueMicrotask(() => {
      setLogoTileBackdrop(classifyLogoTileBackdropFromHtmlImage(img));
    });
  }, []);

  const handleLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      clearLoadingTimeout();
      setIsLoading(false);
      applyIconBackdrop(e.currentTarget);
    },
    [applyIconBackdrop]
  );

  React.useLayoutEffect(() => {
    const img = iconImgRef.current;
    if (!img?.complete || img.naturalWidth === 0 || isLoading) return;
    applyIconBackdrop(img);
  }, [companyKey, srcIndex, isLoading, applyIconBackdrop]);

  React.useEffect(() => {
    setLogoTileBackdrop('green');
  }, [srcIndex]);

  React.useEffect(() => {
    srcIndexRef.current = 0;
    setSrcIndex(0);
    setImgError(false);
    setIsLoading(true);
    setLogoTileBackdrop('green');
    setLoadingTimeout();
    return () => {
      clearLoadingTimeout();
    };
  }, [companyKey, setLoadingTimeout]);

  React.useEffect(() => {
    srcIndexRef.current = srcIndex;
    if (isLoading) {
      setLoadingTimeout();
    }
    return () => {
      clearLoadingTimeout();
    };
  }, [srcIndex, isLoading, setLoadingTimeout]);

  if (!companyKey) {
    const borderRadius =
      variant === 'bare' ? 'inherit' : variant === 'square' ? '0' : variant === 'rounded' ? '0.75rem' : '1rem';
    const shell = [containerShellForVariant(variant), className].filter(Boolean).join(' ');
    return (
      <div
        className={`flex items-center justify-center ${shell} relative`}
        style={{ width: pxSize, height: pxSize, padding: 0, boxSizing: 'border-box', borderRadius }}
      >
        <img
          src="/company-logo-default.svg"
          alt={label}
          width={pxSize}
          height={pxSize}
          className="w-full h-full object-contain"
          loading="lazy"
          decoding="async"
          style={{
            background: 'transparent',
            borderRadius,
            aspectRatio: '1 / 1',
          }}
        />
      </div>
    );
  }

  const shell = [containerShellForVariant(variant), className].filter(Boolean).join(' ');
  const rasterBackdropClass =
    logoTileBackdrop === 'monoLight'
      ? 'bg-jc-logo-mono-light'
      : logoTileBackdrop === 'monoDark'
        ? 'bg-jc-logo-mono-dark'
        : logoTileBackdrop === 'surface'
          ? 'bg-jc-logo-surface'
          : '';

  if (imgError || sources.length === 0) {
    const fallbackPx = Math.max(8, pxSize - 2);
    const fallbackBorderRadius =
      variant === 'bare' ? 'inherit' : variant === 'square' ? '0' : variant === 'rounded' ? '0.75rem' : '1rem';
    return (
      <div
        className={`flex items-center justify-center ${shell} relative`}
        style={{ width: pxSize, height: pxSize, padding: 0, boxSizing: 'border-box', borderRadius: fallbackBorderRadius }}
      >
        <CompanyLogoScriptFallback
          key={`${scriptLine}\0${fallbackPx}\0${formLabel}`}
          sizePx={fallbackPx}
          variant={variant}
          scriptLine={scriptLine}
          formLabel={formLabel}
          theme={isTyomarkkinatori ? 'purple' : 'green'}
          aria-label={label}
        />
      </div>
    );
  }

  const rasterBorderRadius =
    variant === 'bare' ? 'inherit' : variant === 'square' ? 0 : variant === 'rounded' ? '0.75rem' : '1rem';

  return (
    <div
      className={`flex items-center justify-center ${rasterBackdropClass} relative ${shell}`}
      style={{ width: pxSize, height: pxSize, padding: '0', boxSizing: 'border-box', borderRadius: rasterBorderRadius }}
    >
      {isLoading && (
        <div className={`absolute inset-0 flex items-center justify-center ${rasterBackdropClass} z-10`}>
          <div
            className="animate-spin rounded-full border-2 border-jc-border border-t-jc-accent"
            style={{
              width: pxSize * 0.4,
              height: pxSize * 0.4,
            }}
          />
        </div>
      )}
      <img
        ref={iconImgRef}
        src={sources[srcIndex]}
        alt={label}
        width={pxSize}
        height={pxSize}
        className={`w-full h-full object-contain transition-opacity duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onError={handleError}
        onLoad={handleLoad}
        onAbort={handleError}
        loading="lazy"
        style={{
          background: 'transparent',
          borderRadius: rasterBorderRadius,
        }}
      />
    </div>
  );
};
